import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Loader2, 
  CreditCard, 
  Calendar, 
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  ExternalLink
} from 'lucide-react';
import { formatDateDisplay } from '../../utils/dateUtils';

const AdminSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [setDateDialogOpen, setSetDateDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [manualDate, setManualDate] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/admin/subscriptions');
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (schoolId, newStatus) => {
    try {
      await api.patch(`/admin/subscriptions/${schoolId}?subscription_status=${newStatus}`);
      setSubscriptions(prev => prev.map(s => 
        s.school_id === schoolId ? { ...s, subscription_status: newStatus } : s
      ));
      toast.success('Subscription status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExtendSubscription = async () => {
    if (!selectedSchool) return;
    setUpdating(true);
    try {
      const response = await api.post(`/admin/subscriptions/${selectedSchool.school_id}/extend?days=${extendDays}`);
      setSubscriptions(prev => prev.map(s => 
        s.school_id === selectedSchool.school_id 
          ? { ...s, license_expiry_date: response.data.new_expiry_date, subscription_status: 'active' } 
          : s
      ));
      toast.success(`Subscription extended by ${extendDays} days`);
      setExtendDialogOpen(false);
      setSelectedSchool(null);
    } catch (error) {
      toast.error('Failed to extend subscription');
    } finally {
      setUpdating(false);
    }
  };

  const handleSetExpiryDate = async () => {
    if (!selectedSchool || !manualDate) return;
    setUpdating(true);
    try {
      const response = await api.post(`/admin/subscriptions/${selectedSchool.school_id}/set-expiry?expiry_date=${manualDate}`);
      setSubscriptions(prev => prev.map(s => 
        s.school_id === selectedSchool.school_id 
          ? { ...s, license_expiry_date: response.data.new_expiry_date, subscription_status: response.data.subscription_status } 
          : s
      ));
      toast.success(response.data.message);
      setSetDateDialogOpen(false);
      setSelectedSchool(null);
      setManualDate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set expiry date');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status, expiryDate) => {
    // Check if actually expired
    if (expiryDate) {
      const expiry = new Date(expiryDate);
      if (expiry < new Date()) {
        return <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" /> Expired
        </Badge>;
      }
    }

    switch (status) {
      case 'trial':
        return <Badge variant="secondary" className="flex items-center gap-1 bg-amber-100 text-amber-800">
          <Clock className="h-3 w-3" /> Trial
        </Badge>;
      case 'active':
        return <Badge variant="default" className="flex items-center gap-1 bg-emerald-100 text-emerald-800">
          <CheckCircle className="h-3 w-3" /> Active
        </Badge>;
      case 'expired':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" /> Expired
        </Badge>;
      case 'suspended':
        return <Badge variant="outline" className="flex items-center gap-1 text-slate-500">
          <AlertTriangle className="h-3 w-3" /> Suspended
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Calculate summary stats
  const totalSchools = subscriptions.length;
  const activeCount = subscriptions.filter(s => s.subscription_status === 'active').length;
  const trialCount = subscriptions.filter(s => s.subscription_status === 'trial').length;
  const expiredCount = subscriptions.filter(s => {
    if (!s.license_expiry_date) return false;
    return new Date(s.license_expiry_date) < new Date();
  }).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-subscriptions-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-slate-900">Subscription Management</h1>
            <p className="text-slate-500">Manage school licenses and subscription status</p>
          </div>
          <Button variant="outline" onClick={fetchSubscriptions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Schools</p>
              <p className="text-2xl font-bold text-slate-900">{totalSchools}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <p className="text-sm text-slate-500">Active</p>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-slate-500">Trial</p>
              </div>
              <p className="text-2xl font-bold text-amber-600">{trialCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <p className="text-sm text-slate-500">Expired</p>
              </div>
              <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Subscriptions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">School Subscriptions</CardTitle>
            <CardDescription>
              View and manage subscription status for all registered schools
            </CardDescription>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No schools registered yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>License Expiry</TableHead>
                    <TableHead>Days Remaining</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => {
                    const daysRemaining = getDaysRemaining(sub.license_expiry_date);
                    return (
                      <TableRow key={sub.school_id} data-testid={`subscription-row-${sub.school_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{sub.school_name}</p>
                            <p className="text-xs text-slate-500">ID: {sub.school_id.substring(0, 8)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{sub.contact_person || '-'}</p>
                            <p className="text-xs text-slate-500">{sub.contact_email || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(sub.subscription_status, sub.license_expiry_date)}
                        </TableCell>
                        <TableCell>
                          {formatExpiryDate(sub.license_expiry_date)}
                        </TableCell>
                        <TableCell>
                          {daysRemaining !== null ? (
                            <span className={`font-medium ${
                              daysRemaining < 0 ? 'text-red-600' :
                              daysRemaining <= 7 ? 'text-amber-600' :
                              'text-slate-600'
                            }`}>
                              {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days ago` : `${daysRemaining} days`}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Select
                              value={sub.subscription_status || 'trial'}
                              onValueChange={(v) => handleUpdateStatus(sub.school_id, v)}
                            >
                              <SelectTrigger className="w-[120px] h-8" data-testid={`status-select-${sub.school_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="trial">Trial</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="expired">Expired</SelectItem>
                                <SelectItem value="suspended">Suspended</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedSchool(sub);
                                setExtendDialogOpen(true);
                              }}
                              data-testid={`extend-btn-${sub.school_id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Extend
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedSchool(sub);
                                // Pre-fill with current expiry date if exists
                                if (sub.license_expiry_date) {
                                  const date = new Date(sub.license_expiry_date);
                                  setManualDate(date.toISOString().split('T')[0]);
                                } else {
                                  setManualDate('');
                                }
                                setSetDateDialogOpen(true);
                              }}
                              data-testid={`set-date-btn-${sub.school_id}`}
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              Set Date
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Extend Subscription Dialog */}
        <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Extend Subscription</DialogTitle>
              <DialogDescription>
                Extend the license for {selectedSchool?.school_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current Expiry</Label>
                <p className="text-sm text-slate-600">
                  {selectedSchool ? formatExpiryDate(selectedSchool.license_expiry_date) : '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="extend-days">Extend By (days)</Label>
                <Select value={String(extendDays)} onValueChange={(v) => setExtendDays(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days (1 month)</SelectItem>
                    <SelectItem value="90">90 days (3 months)</SelectItem>
                    <SelectItem value="180">180 days (6 months)</SelectItem>
                    <SelectItem value="365">365 days (1 year)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExtendDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleExtendSubscription} 
                disabled={updating}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extending...
                  </>
                ) : (
                  'Extend Subscription'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Set Expiry Date Dialog */}
        <Dialog open={setDateDialogOpen} onOpenChange={setSetDateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Expiry Date</DialogTitle>
              <DialogDescription>
                Manually set the subscription expiry date for {selectedSchool?.school_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current Expiry</Label>
                <p className="text-sm text-slate-600">
                  {selectedSchool ? formatExpiryDate(selectedSchool.license_expiry_date) : '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-date">New Expiry Date</Label>
                <Input
                  id="manual-date"
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  data-testid="manual-expiry-date-input"
                />
                <p className="text-xs text-slate-500">
                  The subscription status will automatically be set to "active" if the date is in the future, 
                  or "expired" if the date is in the past.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSetDateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSetExpiryDate} 
                disabled={updating || !manualDate}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Set Expiry Date'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminSubscriptionsPage;
